import React, { useRef, useState } from 'react';
import { Avatar, IconButton, Paper } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

interface AvatarTooltipProps {
  avatars: Array<{ id: number; src: string }>;
  onSelect: (id: number | null) => void;
  onClose: () => void;
  onConfirm: () => void;
  open: boolean;
}

const AvatarTooltip: React.FC<AvatarTooltipProps> = ({ avatars, onSelect, onClose, onConfirm, open }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 200;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  // גלילה אופקית עם גלגלת
  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft += e.deltaY;
    }
  };

  // גרירה עם האצבע (touch)
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setStartX(e.touches[0].pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.touches[0].pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2; // מהירות הגרירה
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // גרירה עם עכבר (למקרה של touchpad)
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleSelect = (id: number) => {
    setSelectedId((prev) => {
      const newId = prev === id ? null : id;
      onSelect(newId);
      return newId;
    });
  };

  const handleCancel = () => {
    setSelectedId(null);
    onSelect(null);
  };

  if (!open) return null;

  return (
    <Paper className="avatar-tooltip">
      <IconButton onClick={onClose} size="small" className="close-button">
        <CloseIcon fontSize="small" />
      </IconButton>
      <h4>בחר/י דמות</h4>
      <div className="avatar-tooltip-container">
        <IconButton onClick={() => scroll('right')} size="large" className="avatar-tooltip-scroll-left">
          <ArrowForwardIcon fontSize="large" />
        </IconButton>
        <div
          ref={scrollRef}
          className="avatar-tooltip-grid"
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          style={{ cursor: isDragging ? 'grabbing' : 'grab', userSelect: 'none' }}
        >
          {avatars.map((avatar) => (
            <div key={avatar.id} className="avatar-tooltip-item">
              <Avatar
                src={avatar.src}
                className={`avatar-tooltip-avatar${selectedId === avatar.id ? ' selected' : ''}`}
                onClick={() => handleSelect(avatar.id)}
                sx={{ width: 80, height: 80 }}
              />
            </div>
          ))}
        </div>
        <IconButton onClick={() => scroll('left')} size="large" className="avatar-tooltip-scroll-right">
          <ArrowBackIcon fontSize="large" />
        </IconButton>
      </div>
      {selectedId !== null && (
        <div className="avatar-tooltip-actions">
          <IconButton onClick={onConfirm} color="success">
            <CheckIcon />
          </IconButton>
          <IconButton onClick={handleCancel} color="error">
            <CloseIcon />
          </IconButton>
        </div>
      )}
    </Paper>
  );
};

export default AvatarTooltip;
